import {
    ColumnFilters,
    IFilterValue,
    IKeyValuePair,
} from '../components/Entity/interfaces'
import {
    AttributeType,
    INTRANSIT_STORAGE_KEYS,
    MapLayoutMode,
    VALID_MAP_LAYOUTS,
} from '../constants/constants'
import { ColumnOrderListType } from '../redux/slices/entitySlice'
import { InTransitState } from '../redux/slices/inTransitSlice'
import { ColumnsFilterInPayLoad } from '../services/entityServices'
import { buildScopedKey } from './storageUtils'

/**
 * Resolves the persisted In-Transit map layout for the given org/user, falling
 * back to the template-configured default and finally to 'both'.
 */
export const resolveMapLayout = (
    defaultMapLayout: string,
    orgId?: string,
    userId?: string,
): MapLayoutMode => {
    const stored = localStorage.getItem(
        buildScopedKey(INTRANSIT_STORAGE_KEYS.mapLayout, orgId, userId),
    )
    if (stored === 'both' || stored === 'table-only') return stored
    const settingsDefault = defaultMapLayout as MapLayoutMode
    return VALID_MAP_LAYOUTS.includes(settingsDefault)
        ? settingsDefault
        : 'both'
}

export const getShipmentStatusFiltersList = (
    intransitSlice: InTransitState,
): IKeyValuePair[] => {
    const { shipmentAttributes, inTransitSettings } = intransitSlice
    const shipmentStatusAttributeId =
        inTransitSettings?.shipmentStatusAttributeId ?? 'shipmentStatus'
    const shipmentStatusFilter =
        shipmentAttributes
            ?.find((i) => i.id === shipmentStatusAttributeId)
            ?.range?.trim() || '[]'
    const shipmentStatusFilterList: IKeyValuePair[] =
        JSON.parse(shipmentStatusFilter)
    return shipmentStatusFilterList
}

export const getShipmentStatusFilters = (
    filterList: Record<string, IFilterValue>,
    shipmentStatusAttributeId: string,
): string => {
    return filterList?.[shipmentStatusAttributeId]?.values.join(',') || ''
}

export const getAlarmStatusFilters = (
    filterList: Record<string, IFilterValue>,
    alarmStatusAttributeId: string,
): string => {
    return filterList?.[alarmStatusAttributeId]?.values.join(',') || ''
}

export const getColumnFilters = (
    columnFilters: ColumnFilters[],
    columnVisibilityList: ColumnOrderListType[],
): ColumnsFilterInPayLoad[] => {
    let updatedColumnFilters: ColumnsFilterInPayLoad[] = []
    if (columnVisibilityList?.length) {
        for (const i of columnFilters) {
            if (i.id && i.value?.length) {
                const dataType =
                    columnVisibilityList.find((c) => c.id === i.id)?.dataType ||
                    ''
                const columnName =
                    columnVisibilityList.find((c) => c.id === i.id)
                        ?.columnName || ''
                let updatedValues: any = ''
                if (dataType.toLowerCase() === AttributeType.ENTITY) {
                    updatedValues = i.value?.length
                        ? i.value?.map((i: any) => i.entityId)
                        : null
                } else if (
                    dataType.toLowerCase() ===
                        AttributeType.SENSOR_SNAPSHOT_READINGS ||
                    dataType.toLowerCase() === AttributeType.CREATION_DATETIME
                ) {
                    if (i.value?.[0] && i.value?.[1]) {
                        updatedColumnFilters = [
                            ...updatedColumnFilters,
                            {
                                attributeId: i.id,
                                fromDate: i.value?.[0],
                                toDate: i.value?.[1],
                            },
                        ]
                    }
                    continue
                } else if (columnName === 'Organization Name') {
                    updatedValues = i.value?.length
                        ? i.value?.map((org: any) => org.orgId)
                        : null
                } else {
                    updatedValues = i.value
                }
                updatedColumnFilters = [
                    ...updatedColumnFilters,
                    {
                        attributeId: i.id,
                        values: updatedValues,
                    },
                ]
            }
        }
    }
    return updatedColumnFilters
}

/** shipmentStatus attribute value that marks a shipment as completed. */
export const COMPLETED_SHIPMENT_STATUS = 'Completed'

/** Minimal row shape needed to decide bulk-completion eligibility. */
export interface SelectableShipmentRow {
    entityId?: string
    attributes?: Record<string, unknown>
}

/**
 * Whether the current row selection contains at least one already-completed
 * shipment — i.e. a row whose shipmentStatus attribute equals "Completed".
 * Used to disable the "Complete Selected" bulk action for the whole selection
 * when any completed shipment is included (UBQ-5245).
 */
export const selectionHasCompletedShipment = (
    rowSelection: Record<string, boolean>,
    flatData: SelectableShipmentRow[],
    shipmentStatusAttributeId: string,
): boolean => {
    for (const key of Object.keys(rowSelection)) {
        if (!rowSelection[key]) continue
        const row = flatData[Number(key)]
        if (
            row?.attributes?.[shipmentStatusAttributeId] ===
            COMPLETED_SHIPMENT_STATUS
        ) {
            return true
        }
    }
    return false
}
